import * as _ from 'lodash-es';
import { Delta } from 'quill/core';
import React from 'react';
import Card from '../components/Card';
import DeltaPreview from '../components/DeltaPreview';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { useGetUser } from '../user/useAllUsers';
import Link from '../utils/router/Link';
import historyPath from '../utils/url/historyPath';
import postPath from '../utils/url/postPath';
import userPath from '../utils/url/userPath';
import Activity from './Activity';
import activitiesPostTopics from './activitiesPostTopics';

const ACTIVITY_CREATED_HISTORY_ACTIVITY_FRAGMENT = gql(`
  fragment ActivityCreatedHistory_activity on CreatedHistoryActivity {
    actedAt
    actor {
      ...useGetUser_user
    }
    createdHistory {
      id
      diff
      snapshot
      versionFrom
      versionTo
      post {
        id
        title
        ...postPath_post
        ...historyPath_post
        topics {
          ...activitiesPostTopics_topics
        }
      }
    }
  }
`);

interface ActivityCreatedHistoryProps {
  activity: DocumentType<typeof ACTIVITY_CREATED_HISTORY_ACTIVITY_FRAGMENT>;
}

const ActivityCreatedHistory = ({ activity }: ActivityCreatedHistoryProps) => {
  const { actedAt, createdHistory } = activity;

  const getUser = useGetUser();

  if (!activity.actor) {
    return null;
  }

  const actor = getUser(activity.actor);

  const post = createdHistory?.post;
  const path = post && postPath(post);

  const snapshot = new Delta(createdHistory?.snapshot);
  const diff = new Delta(createdHistory?.diff);
  let changeStart = 0;
  let changedLines = 0;
  const content = snapshot.compose(diff);
  content.eachLine((delta) => {
    const hasEdits = delta.ops.some((op) => op.attributes?.diff);
    if (hasEdits) {
      changedLines += 1;
    } else if (changedLines === 0) {
      changeStart += delta.length() + 1;
    }
  });
  if (changedLines <= 0) return null;

  const postHistoryPath =
    post &&
    !_.isNil(createdHistory.versionFrom) &&
    !_.isNil(createdHistory.versionTo)
      ? historyPath(
          createdHistory.post,
          createdHistory.versionFrom,
          createdHistory.versionTo,
        )
      : null;

  const cardLabel = post?.title && `History for post ${post.title}`;

  return (
    <Activity
      actor={actor}
      actedAt={actedAt}
      action="POST_EDIT"
      title={
        <>
          <Link to={userPath(actor)}>{actor?.name}</Link> edited{' '}
          {path && <Link to={path}>{post.title}</Link>}
        </>
      }
      meta={activitiesPostTopics(
        createdHistory?.post ? createdHistory.post.topics : null,
      )}
      metaRight={
        changedLines > 1 && postHistoryPath ? (
          <Link to={postHistoryPath}>See {changedLines - 1} more</Link>
        ) : undefined
      }
    >
      <Card to={postHistoryPath ?? undefined} aria-label={cardLabel}>
        <DeltaPreview
          typography="activity"
          content={content.slice(changeStart)}
          maxLines={5}
          link={false}
        />
      </Card>
    </Activity>
  );
};

export default ActivityCreatedHistory;
